export const CANVAS_ZOOM_LIMITS = Object.freeze({
  MAX: 8,
  MIN: 0.1,
  STEP: 1.15,
});

/**
 * @typedef {{ x: number, y: number, zoom: number }} CanvasViewport
 * @typedef {{ x: number, y: number }} CanvasPoint
 */

/**
 * @param {{ point: CanvasPoint, viewport: CanvasViewport }} params
 * @returns {CanvasPoint} scene coordinates
 */
export const screenToScene = ({ point, viewport }) => ({
  x: (point.x - viewport.x) / viewport.zoom,
  y: (point.y - viewport.y) / viewport.zoom,
});

/**
 * @param {{ point: CanvasPoint, viewport: CanvasViewport }} params
 * @returns {CanvasPoint} screen coordinates in CSS pixels
 */
export const sceneToScreen = ({ point, viewport }) => ({
  x: point.x * viewport.zoom + viewport.x,
  y: point.y * viewport.zoom + viewport.y,
});

/**
 * @param {number} zoom
 * @returns {number}
 */
export const clampZoom = (zoom) =>
  Math.min(Math.max(zoom, CANVAS_ZOOM_LIMITS.MIN), CANVAS_ZOOM_LIMITS.MAX);

/**
 * @param {{ delta: CanvasPoint, viewport: CanvasViewport }} params
 * @returns {CanvasViewport}
 */
export const panViewport = ({ delta, viewport }) => ({
  ...viewport,
  x: viewport.x + delta.x,
  y: viewport.y + delta.y,
});

/**
 * Zooms while keeping the scene point under `screenPoint` stationary.
 * @param {{ screenPoint: CanvasPoint, viewport: CanvasViewport, zoom: number }} params
 * @returns {CanvasViewport}
 */
export const zoomViewportAtPoint = ({ screenPoint, viewport, zoom }) => {
  const nextZoom = clampZoom(zoom);
  const scenePoint = screenToScene({ point: screenPoint, viewport });

  return {
    x: screenPoint.x - scenePoint.x * nextZoom,
    y: screenPoint.y - scenePoint.y * nextZoom,
    zoom: nextZoom,
  };
};

/**
 * @param {{ direction: number, screenPoint: CanvasPoint, viewport: CanvasViewport }} params
 * @returns {CanvasViewport}
 */
export const stepViewportZoom = ({ direction, screenPoint, viewport }) => {
  const factor =
    direction > 0 ? CANVAS_ZOOM_LIMITS.STEP : 1 / CANVAS_ZOOM_LIMITS.STEP;

  return zoomViewportAtPoint({
    screenPoint,
    viewport,
    zoom: viewport.zoom * factor,
  });
};

// Nominal screen-space center used for a stage axis the DOM has not measured
// yet; keeps the largest fitted image insert (480x360 screen px) fully on the
// stage once the real size arrives.
const UNMEASURED_STAGE_FALLBACK_CENTER = Object.freeze({ x: 320, y: 240 });

/**
 * Screen-space center of the stage. Layout can report a zero dimension
 * briefly after mount, before the ResizeObserver delivers the real rect, so
 * an unmeasured axis falls back to a nominal center instead of pushing
 * inserts against the stage edge.
 * @param {{ width: number, height: number }} size
 * @returns {CanvasPoint} screen coordinates in CSS pixels
 */
export const getStageCenterScreenPoint = (size) => ({
  x: size.width > 0 ? size.width / 2 : UNMEASURED_STAGE_FALLBACK_CENTER.x,
  y: size.height > 0 ? size.height / 2 : UNMEASURED_STAGE_FALLBACK_CENTER.y,
});

/**
 * Scene-space rectangle currently visible, expanded by `margin` scene units.
 * Used for render culling.
 * @param {{ margin?: number, size: { width: number, height: number }, viewport: CanvasViewport }} params
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export const getVisibleSceneRect = ({ margin = 0, size, viewport }) => ({
  height: size.height / viewport.zoom + margin * 2,
  width: size.width / viewport.zoom + margin * 2,
  x: -viewport.x / viewport.zoom - margin,
  y: -viewport.y / viewport.zoom - margin,
});
